import React from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Calendar,
  Factory,
  LayoutGrid,
  MapPin,
  Megaphone,
  Target,
  Trash2,
  Users,
  Pencil,
} from "lucide-react";
import ProspectProfileRow from "../components/ui/Avatar/ProspectProfileRow";
import { StatusChip } from "../components/ui/Status/StatusChip";
import { EngagementScoreBreakdown } from "../components/ui/Status/EngagementScoreBreakdown";
import {
  getAggregateMatchFitMeta,
  getEngagementStatusVariant,
  getMatchScoreFitMeta,
  getTextInCapitalize,
  getLeadScaleScorePillClasses,
  getMatchScoreFitKeyPillClasses,
} from "./helperFunctions";
import LinkedinButton from "../components/ui/Button/LinkedinButton";
import GenerateMatchScore from "../components/ui/Button/GenerateMatchScore";
import CommonDropdownMenu from "../components/ui/DropDown/DropdownMenu";

// render reasoning with bold quotes
export function renderReasoningWithBoldQuotes(text, keyPrefix = "qt") {
  if (text == null || typeof text !== "string") return text;
  const re =
    /\u201c([^\u201d]*)\u201d|"([^"]*)"|\u2018([^\u2019]{2,})\u2019|(?<![A-Za-z])'([^']{2,})'(?![A-Za-z])/g;
  const nodes = [];
  let last = 0;
  let k = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const inner = m[1] ?? m[2] ?? m[3] ?? m[4] ?? "";
    nodes.push(
      <strong
        key={`${keyPrefix}-bq-${k++}`}
        className="font-semibold text-gray-900 dark:text-gray-100"
      >
        {inner}
      </strong>,
    );
    last = m.index + m[0].length;
  }
  if (nodes.length === 0) return text;
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function parseMatchScoreReasoningSections(text) {
  if (!text || typeof text !== "string") return null;

  const normalized = text.replace(/\r\n/g, "\n").trim();
  const markerRegex =
    /(?:^|\n|\s*[-•]\s*|\s+)(Firmographics|Technographics|ML|Engagement|Intent|Fit):\s*/gi;
  const markers = [];
  let match;

  while ((match = markerRegex.exec(normalized)) !== null) {
    markers.push({
      label: match[1],
      contentStart: match.index + match[0].length,
      matchStart: match.index,
    });
  }

  if (!markers.length) return null;

  return markers.map((marker, index) => {
    const contentEnd =
      index + 1 < markers.length
        ? markers[index + 1].matchStart
        : normalized.length;
    return {
      label: marker.label,
      content: normalized.slice(marker.contentStart, contentEnd).trim(),
    };
  });
}

export function renderMatchScoreReasoning(text, keyPrefix = "reason") {
  const sections = parseMatchScoreReasoningSections(text);
  if (!sections?.length) {
    return renderReasoningWithBoldQuotes(text, keyPrefix);
  }

  return (
    <div className="flex flex-col gap-2.5">
      {sections.map((section, index) => (
        <p key={`${keyPrefix}-${section.label}-${index}`} className="leading-relaxed">
          <span className="font-semibold text-gray-800 dark:text-gray-200">{section.label}:</span>{" "}
          <span className="text-gray-600 dark:text-gray-300">
            {renderReasoningWithBoldQuotes(
              section.content,
              `${keyPrefix}-${section.label}-${index}`,
            )}
          </span>
        </p>
      ))}
    </div>
  );
}

export const ContactColumns = [
  { uid: "name", name: "Name", sortable: true, align: "left", width: "240px" },
  {
    uid: "company",
    name: "Company",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "companySize",
    name: "Employees",
    sortable: true,
    align: "left",
    width: "140px",
  },
  {
    uid: "position",
    name: "Designation",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "matchScore",
    name: "Match Score",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "currentlyEnrolledCampaignName",
    name: "Enrolled Campaign",
    sortable: true,
    align: "left",
    width: "180px",
  },
  {
    uid: "industry",
    name: "Industry",
    sortable: true,
    align: "left",
    width: "140px",
  },
  {
    uid: "engagementScore",
    name: "Engagement Score",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "location",
    name: "Location",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "linkedinUrl",
    name: "LinkedIn URL",
    sortable: true,
    align: "left",
    width: "160px",
    linkable: true,
  },
  {
    uid: "whatsappNumber",
    name: "Phone/WhatsApp",
    sortable: true,
    align: "left",
    width: "160px",
  },
  {
    uid: "emailConsentStatus",
    name: "Email Consent",
    sortable: true,
    align: "left",
    width: "150px",
  },
  {
    uid: "createdAt",
    name: "Create/Modified",
    sortable: true,
    align: "left",
    width: "130px",
  },

  {
    uid: "actions",
    name: "Actions",
    sortable: false,
    align: "center",
    width: "60px",
  },
];

export const ContactFilterFields = [
  {
    id: 1,
    key: "company",
    title: "Company",
    icon: <Building2 size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 2,
    key: "designation",
    title: "Designation",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 3,
    key: "matchScoreFit",
    title: "Match Score Fit",
    icon: <Target size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "checkboxMulti",
    options: [
      { label: "Strong Fit (> 80)", value: "strong" },
      { label: "Medium Fit (50-80)", value: "moderate" },
      { label: "No Fit (< 50)", value: "not_fit" },
      { label: "Unavailable (0)", value: "unavailable" },
    ],
  },
  {
    id: 4,
    key: "industry",
    title: "Industry",
    icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 5,
    key: "country",
    title: "Country",
    icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 6,
    key: "companySize",
    title: "Company Size",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "checkboxMulti",
    options: [
      { label: "1-50", value: "1-50" },
      { label: "51-200", value: "51-200" },
      { label: "201-500", value: "201-500" },
      { label: "501-1000", value: "501-1000" },
      { label: "1000+", value: "1000+" },
    ],
  },
  {
    id: 7,
    key: "enrollmentStatus",
    title: "Enrollment Status",
    icon: <Megaphone size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "checkboxSingle",
    options: [
      { label: "Enrolled in Campaign", value: "enrolled" },
      { label: "Not Enrolled", value: "not_enrolled" },
    ],
  },
  {
    id: 8,
    key: "dateCreated",
    title: "Date Created",
    icon: <Calendar size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "checkboxMulti",
    options: [
      { label: "Today", value: "today" },
      { label: "Last 7 Days", value: "last_7_days" },
      { label: "Last 30 Days", value: "last_30_days" },
      { label: "Last 90 Days", value: "last_90_days" },
    ],
  },
];

export const ContactOutFilterFields = [
  {
    id: 1,
    key: "job_title",
    title: "Job Titles",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 2,
    key: "location",
    title: "Work Locations",
    icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 3,
    key: "seniority",
    title: "Seniority",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Type & press enter to add it!",
  },
  {
    id: 4,
    key: "company_size",
    title: "Company Size",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "range",
    options: [
      { label: "1-10", value: "1_10" },
      { label: "11-50", value: "11_50" },
      { label: "51-200", value: "51_200" },
      { label: "201-500", value: "201_500" },
      { label: "501-1K", value: "501_1000" },
      { label: "1K-5K", value: "1001_5000" },
      { label: "5K-10K", value: "5001_10000" },
      { label: "10K+", value: "10001" },
    ],
  },
  {
    id: 5,
    key: "has_work_email",
    title: "Has Work Email",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "toggle",
  },
  {
    id: 6,
    key: "include_related_job_titles",
    title: "Include Related Titles",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "toggle",
  },
];

export const DEFAULT_FILTERS = {
  company: [],
  designation: [],
  matchScoreFit: [],
  industry: [],
  country: [],
  companySize: [],
  enrollmentStatus: [],
  dateCreated: [],
};

export const CONTACTOUT_DEFAULT_FILTERS = {
  job_title: [],
  location: [],
  seniority: [],
  company_size: [],
  include_related_job_titles: true,
  has_work_email: true,
};

// Compatibility aliases for components still being migrated from the Apollo naming.
export const ApolloFilterFields = ContactOutFilterFields;
export const APOLLO_DEFAULT_FILTERS = CONTACTOUT_DEFAULT_FILTERS;

export const linkedinCategories = [
  {
    label: "abrasives and nonmetallic minerals manufacturing",
    value: "abrasives and nonmetallic minerals manufacturing"
  },
  {
    label: "accessible architecture and design",
    value: "accessible architecture and design"
  },
  {
    label: "accessible hardware manufacturing",
    value: "accessible hardware manufacturing"
  },
  {
    label: "accommodation and food services",
    value: "accommodation and food services"
  },
  {
    label: "accomodation services",
    value: "accomodation services"
  },
  {
    label: "accounting",
    value: "accounting"
  },
  {
    label: "administration of justice",
    value: "administration of justice"
  },
  {
    label: "administrative and support services",
    value: "administrative and support services"
  },
  {
    label: "advertising services",
    value: "advertising services"
  },
  {
    label: "agricultural chemical manufacturing",
    value: "agricultural chemical manufacturing"
  },
  {
    label: "agriculture, construction, mining machinery manufacturing",
    value: "agriculture construction mining machinery manufacturing"
  },
  {
    label: "air, water, and waste program management",
    value: "air water and waste program management"
  },
  {
    label: "airlines and aviation",
    value: "airlines and aviation"
  },
  {
    label: "alternative dispute resolution",
    value: "alternative dispute resolution"
  },
  {
    label: "alternative fuel vehicle manufacturing",
    value: "alternative fuel vehicle manufacturing"
  },
  {
    label: "alternative medicine",
    value: "alternative medicine"
  },
  {
    label: "ambulance services",
    value: "ambulance services"
  },
  {
    label: "amusement parks and arcades",
    value: "amusement parks and arcades"
  },
  {
    label: "animal feed manufacturing",
    value: "animal feed manufacturing"
  },
  {
    label: "animation",
    value: "animation"
  },
  {
    label: "animation and post-production",
    value: "animation and post-production"
  },
  {
    label: "apparel & fashion",
    value: "apparel and fashion"
  },
  {
    label: "apparel manufacturing",
    value: "apparel manufacturing"
  },
  {
    label: "appliances, electrical, and electronics manufacturing",
    value: "appliances electrical and electronics manufacturing"
  },
  {
    label: "architectural and structural metal manufacturing",
    value: "architectural and structural metal manufacturing"
  },
  {
    label: "architecture and planning",
    value: "architecture and planning"
  },
  {
    label: "armed forces",
    value: "armed forces"
  },
  {
    label: "artificial rubber and synthetic fiber manufacturing",
    value: "artificial rubber and synthetic fiber manufacturing"
  },
  {
    label: "artists and writers",
    value: "artists and writers"
  },
  {
    label: "arts & crafts",
    value: "arts and crafts"
  },
  {
    label: "arts and crafts",
    value: "arts and crafts"
  },
  {
    label: "audio and video equipment manufacturing",
    value: "audio and video equipment manufacturing"
  },
  {
    label: "automation machinery manufacturing",
    value: "automation machinery manufacturing"
  },
  {
    label: "automotive",
    value: "automotive"
  },
  {
    label: "aviation & aerospace",
    value: "aviation and aerospace"
  },
  {
    label: "aviation and aerospace component manufacturing",
    value: "aviation and aerospace component manufacturing"
  },
  {
    label: "baked goods manufacturing",
    value: "baked goods manufacturing"
  },
  {
    label: "banking",
    value: "banking"
  },
  {
    label: "bars, taverns, and nightclubs",
    value: "bars taverns and nightclubs"
  },
  {
    label: "bed-and-breakfasts, hostels, homestays",
    value: "bed-and-breakfasts hostels homestays"
  },
  {
    label: "beverage manufacturing",
    value: "beverage manufacturing"
  },
  {
    label: "biomass electric power generation",
    value: "biomass electric power generation"
  },
  {
    label: "biotechnology",
    value: "biotechnology"
  },
  {
    label: "biotechnology research",
    value: "biotechnology research"
  },
  {
    label: "blockchain services",
    value: "blockchain services"
  },
  {
    label: "blogs",
    value: "blogs"
  },
  {
    label: "boilers, tanks, and shipping container manufacturing",
    value: "boilers tanks and shipping container manufacturing"
  },
  {
    label: "book and periodical publishing",
    value: "book and periodical publishing"
  },
  {
    label: "book publishing",
    value: "book publishing"
  },
  {
    label: "breweries",
    value: "breweries"
  },
  {
    label: "broadcast media production and distribution",
    value: "broadcast media production and distribution"
  },
  {
    label: "building construction",
    value: "building construction"
  },
  {
    label: "building equipment contractors",
    value: "building equipment contractors"
  },
  {
    label: "building finishing contractors",
    value: "building finishing contractors"
  },
  {
    label: "building materials",
    value: "building materials"
  },
  {
    label: "building structure and exterior contractors",
    value: "building structure and exterior contractors"
  },
  {
    label: "business consulting and services",
    value: "business consulting and services"
  },
  {
    label: "business content",
    value: "business content"
  },
  {
    label: "business intelligence platforms",
    value: "business intelligence platforms"
  },
  {
    label: "business supplies & equipment",
    value: "business supplies and equipment"
  },
  {
    label: "cable and satellite programming",
    value: "cable and satellite programming"
  },
  {
    label: "capital markets",
    value: "capital markets"
  },
  {
    label: "caterers",
    value: "caterers"
  },
  {
    label: "central bank",
    value: "central bank"
  },
  {
    label: "chemical manufacturing",
    value: "chemical manufacturing"
  },
  {
    label: "chemical raw materials manufacturing",
    value: "chemical raw materials manufacturing"
  },
  {
    label: "child day care services",
    value: "child day care services"
  },
  {
    label: "chiropractors",
    value: "chiropractors"
  },
  {
    label: "circuses and magic shows",
    value: "circuses and magic shows"
  },
  {
    label: "civic and social organizations",
    value: "civic and social organizations"
  },
  {
    label: "civil engineering",
    value: "civil engineering"
  },
  {
    label: "claims adjusting, actuarial services",
    value: "claims adjusting actuarial services"
  },
  {
    label: "clay and refractory products manufacturing",
    value: "clay and refractory products manufacturing"
  },
  {
    label: "climate data and analytics",
    value: "climate data and analytics"
  },
  {
    label: "climate technology product manufacturing",
    value: "climate technology product manufacturing"
  },
  {
    label: "coal mining",
    value: "coal mining"
  },
  {
    label: "collection agencies",
    value: "collection agencies"
  },
  {
    label: "commercial and industrial equipment rental",
    value: "commercial and industrial equipment rental"
  },
  {
    label: "commercial and industrial machinery maintenance",
    value: "commercial and industrial machinery maintenance"
  },
  {
    label: "commercial and service industry machinery manufacturing",
    value: "commercial and service industry machinery manufacturing"
  },
  {
    label: "commercial real estate",
    value: "commercial real estate"
  },
  {
    label: "communications equipment manufacturing",
    value: "communications equipment manufacturing"
  },
  {
    label: "community development and urban planning",
    value: "community development and urban planning"
  },
  {
    label: "community services",
    value: "community services"
  },
  {
    label: "computer and network security",
    value: "computer and network security"
  },
  {
    label: "computer games",
    value: "computer games"
  },
  {
    label: "computer hardware",
    value: "computer hardware"
  },
  {
    label: "computer hardware manufacturing",
    value: "computer hardware manufacturing"
  },
  {
    label: "computer networking",
    value: "computer networking"
  },
  {
    label: "computer networking products",
    value: "computer networking products"
  },
  {
    label: "computers and electronics manufacturing",
    value: "computers and electronics manufacturing"
  },
  {
    label: "conservation programs",
    value: "conservation programs"
  },
  {
    label: "construction",
    value: "construction"
  },
  {
    label: "construction hardware manufacturing",
    value: "construction hardware manufacturing"
  },
  {
    label: "consumer electronics",
    value: "consumer electronics"
  },
  {
    label: "consumer goods",
    value: "consumer goods"
  },
  {
    label: "consumer goods rental",
    value: "consumer goods rental"
  },
  {
    label: "consumer services",
    value: "consumer services"
  },
  {
    label: "correctional institutions",
    value: "correctional institutions"
  },
  {
    label: "cosmetics",
    value: "cosmetics"
  },
  {
    label: "cosmetology and barber schools",
    value: "cosmetology and barber schools"
  },
  {
    label: "courts of law",
    value: "courts of law"
  },
  {
    label: "credit intermediation",
    value: "credit intermediation"
  },
  {
    label: "cutlery and handtool manufacturing",
    value: "cutlery and handtool manufacturing"
  },
  {
    label: "dairy",
    value: "dairy"
  },
  {
    label: "dairy product manufacturing",
    value: "dairy product manufacturing"
  },
  {
    label: "dance companies",
    value: "dance companies"
  },
  {
    label: "data infrastructure and analytics",
    value: "data infrastructure and analytics"
  },
  {
    label: "data security software products",
    value: "data security software products"
  },
  {
    label: "death care services",
    value: "death care services"
  },
  {
    label: "defense & space",
    value: "defense and space"
  },
  {
    label: "defense and space manufacturing",
    value: "defense and space manufacturing"
  },
  {
    label: "dentists",
    value: "dentists"
  },
  {
    label: "design",
    value: "design"
  },
  {
    label: "design services",
    value: "design services"
  },
  {
    label: "desktop computing software products",
    value: "desktop computing software products"
  },
  {
    label: "digital accessibility services",
    value: "digital accessibility services"
  },
  {
    label: "distilleries",
    value: "distilleries"
  },
  {
    label: "e-learning",
    value: "e-learning"
  },
  {
    label: "e-learning providers",
    value: "e-learning providers"
  },
  {
    label: "economic programs",
    value: "economic programs"
  },
  {
    label: "education",
    value: "education"
  },
  {
    label: "education administration programs",
    value: "education administration programs"
  },
  {
    label: "education management",
    value: "education management"
  },
  {
    label: "electric lighting equipment manufacturing",
    value: "electric lighting equipment manufacturing"
  },
  {
    label: "electric power generation",
    value: "electric power generation"
  },
  {
    label: "electric power transmission, control, and distribution",
    value: "electric power transmission control and distribution"
  },
  {
    label: "electrical equipment manufacturing",
    value: "electrical equipment manufacturing"
  },
  {
    label: "electronic and precision equipment maintenance",
    value: "electronic and precision equipment maintenance"
  },
  {
    label: "embedded software products",
    value: "embedded software products"
  },
  {
    label: "emergency and relief services",
    value: "emergency and relief services"
  },
  {
    label: "energy technology",
    value: "energy technology"
  },
  {
    label: "engineering services",
    value: "engineering services"
  },
  {
    label: "engines and power transmission equipment manufacturing",
    value: "engines and power transmission equipment manufacturing"
  },
  {
    label: "entertainment",
    value: "entertainment"
  },
  {
    label: "entertainment providers",
    value: "entertainment providers"
  },
  {
    label: "environmental quality programs",
    value: "environmental quality programs"
  },
  {
    label: "environmental services",
    value: "environmental services"
  },
  {
    label: "equipment rental services",
    value: "equipment rental services"
  },
  {
    label: "event services",
    value: "event services"
  },
  {
    label: "events services",
    value: "events services"
  },
  {
    label: "executive offices",
    value: "executive offices"
  },
  {
    label: "executive search services",
    value: "executive search services"
  },
  {
    label: "fabricated metal products",
    value: "fabricated metal products"
  },
  {
    label: "facilities services",
    value: "facilities services"
  },
  {
    label: "family planning centers",
    value: "family planning centers"
  },
  {
    label: "farming",
    value: "farming"
  },
  {
    label: "farming, ranching and fisheries",
    value: "farming ranching and fisheries"
  },
  {
    label: "farming, ranching and fisheries",
    value: "farming ranching and fisheries"
  },
  {
    label: "farming, ranching, and fisheries",
    value: "farming ranching and fisheries"
  },
  {
    label: "farming, ranching, forestry",
    value: "farming ranching forestry"
  },
  {
    label: "farming,ranching and fisheries",
    value: "farming ranching and fisheries"
  },
  {
    label: "fashion accessories manufacturing",
    value: "fashion accessories manufacturing"
  },
  {
    label: "financial services",
    value: "financial services"
  },
  {
    label: "fine art",
    value: "fine art"
  },
  {
    label: "fine arts schools",
    value: "fine arts schools"
  },
  {
    label: "fire protection",
    value: "fire protection"
  },
  {
    label: "fisheries",
    value: "fisheries"
  },
  {
    label: "fishing",
    value: "fishing"
  },
  {
    label: "fishing club",
    value: "fishing club"
  },
  {
    label: "flight training",
    value: "flight training"
  },
  {
    label: "food & beverages",
    value: "food and beverages"
  },
  {
    label: "food and beverage manufacturing",
    value: "food and beverage manufacturing"
  },
  {
    label: "food and beverage retail",
    value: "food and beverage retail"
  },
  {
    label: "food and beverage services",
    value: "food and beverage services"
  },
  {
    label: "food production",
    value: "food production"
  },
  {
    label: "footwear and leather goods repair",
    value: "footwear and leather goods repair"
  },
  {
    label: "footwear manufacturing",
    value: "footwear manufacturing"
  },
  {
    label: "forestry and logging",
    value: "forestry and logging"
  },
  {
    label: "fossil fuel electric power generation",
    value: "fossil fuel electric power generation"
  },
  {
    label: "freight and package transportation",
    value: "freight and package transportation"
  },
  {
    label: "fruit and vegetable preserves manufacturing",
    value: "fruit and vegetable preserves manufacturing"
  },
  {
    label: "fuel cell manufacturing",
    value: "fuel cell manufacturing"
  },
  {
    label: "fundraising",
    value: "fundraising"
  },
  {
    label: "funds and trusts",
    value: "funds and trusts"
  },
  {
    label: "funeral services",
    value: "funeral services"
  },
  {
    label: "furniture",
    value: "furniture"
  },
  {
    label: "furniture and home furnishings manufacturing",
    value: "furniture and home furnishings manufacturing"
  },
  {
    label: "gambling facilities and casinos",
    value: "gambling facilities and casinos"
  },
  {
    label: "geothermal electric power generation",
    value: "geothermal electric power generation"
  },
  {
    label: "glass product manufacturing",
    value: "glass product manufacturing"
  },
  {
    label: "glass, ceramics and concrete manufacturing",
    value: "glass ceramics and concrete manufacturing"
  },
  {
    label: "golf courses and country clubs",
    value: "golf courses and country clubs"
  },
  {
    label: "government administration",
    value: "government administration"
  },
  {
    label: "government relations",
    value: "government relations"
  },
  {
    label: "government relations services",
    value: "government relations services"
  },
  {
    label: "graphic design",
    value: "graphic design"
  },
  {
    label: "ground passenger transportation",
    value: "ground passenger transportation"
  },
  {
    label: "health and human services",
    value: "health and human services"
  },
  {
    label: "health, wellness & fitness",
    value: "health wellness and fitness"
  },
  {
    label: "healthcare",
    value: "healthcare"
  },
  {
    label: "higher education",
    value: "higher education"
  },
  {
    label: "highway, street, and bridge construction",
    value: "highway street and bridge construction"
  },
  {
    label: "historical sites",
    value: "historical sites"
  },
  {
    label: "holding companies",
    value: "holding companies"
  },
  {
    label: "home health care services",
    value: "home health care services"
  },
  {
    label: "horticulture",
    value: "horticulture"
  },
  {
    label: "hospitality",
    value: "hospitality"
  },
  {
    label: "hospitals",
    value: "hospitals"
  },
  {
    label: "hospitals and health care",
    value: "hospitals and health care"
  },
  {
    label: "hotels and motels",
    value: "hotels and motels"
  },
  {
    label: "household and institutional furniture manufacturing",
    value: "household and institutional furniture manufacturing"
  },
  {
    label: "household appliance manufacturing",
    value: "household appliance manufacturing"
  },
  {
    label: "household services",
    value: "household services"
  },
  {
    label: "housing and community development",
    value: "housing and community development"
  },
  {
    label: "housing programs",
    value: "housing programs"
  },
  {
    label: "human resources",
    value: "human resources"
  },
  {
    label: "human resources services",
    value: "human resources services"
  },
  {
    label: "hvac and refrigeration equipment manufacturing",
    value: "hvac and refrigeration equipment manufacturing"
  },
  {
    label: "hydroelectric power generation",
    value: "hydroelectric power generation"
  },
  {
    label: "import & export",
    value: "import and export"
  },
  {
    label: "individual and family services",
    value: "individual and family services"
  },
  {
    label: "industrial automation",
    value: "industrial automation"
  },
  {
    label: "industrial machinery manufacturing",
    value: "industrial machinery manufacturing"
  },
  {
    label: "industry associations",
    value: "industry associations"
  },
  {
    label: "information services",
    value: "information services"
  },
  {
    label: "information technology & services",
    value: "information technology and services"
  },
  {
    label: "insurance",
    value: "insurance"
  },
  {
    label: "insurance agencies and brokerages",
    value: "insurance agencies and brokerages"
  },
  {
    label: "insurance and employee benefit funds",
    value: "insurance and employee benefit funds"
  },
  {
    label: "insurance carriers",
    value: "insurance carriers"
  },
  {
    label: "interior design",
    value: "interior design"
  },
  {
    label: "international affairs",
    value: "international affairs"
  },
  {
    label: "international trade and development",
    value: "international trade and development"
  },
  {
    label: "internet cafe",
    value: "internet cafe"
  },
  {
    label: "internet marketplace platforms",
    value: "internet marketplace platforms"
  },
  {
    label: "internet news",
    value: "internet news"
  },
  {
    label: "internet publishing",
    value: "internet publishing"
  },
  {
    label: "interurban and rural bus services",
    value: "interurban and rural bus services"
  },
  {
    label: "investment advice",
    value: "investment advice"
  },
  {
    label: "investment banking",
    value: "investment banking"
  },
  {
    label: "investment management",
    value: "investment management"
  },
  {
    label: "it services and it consulting",
    value: "it services and it consulting"
  },
  {
    label: "it system custom software development",
    value: "it system custom software development"
  },
  {
    label: "it system data services",
    value: "it system data services"
  },
  {
    label: "it system design services",
    value: "it system design services"
  },
  {
    label: "it system installation and disposal",
    value: "it system installation and disposal"
  },
  {
    label: "it system operations and maintenance",
    value: "it system operations and maintenance"
  },
  {
    label: "it system testing and evaluation",
    value: "it system testing and evaluation"
  },
  {
    label: "it system training and support",
    value: "it system training and support"
  },
  {
    label: "janitorial services",
    value: "janitorial services"
  },
  {
    label: "landscaping services",
    value: "landscaping services"
  },
  {
    label: "language schools",
    value: "language schools"
  },
  {
    label: "laundry and drycleaning services",
    value: "laundry and drycleaning services"
  },
  {
    label: "law enforcement",
    value: "law enforcement"
  },
  {
    label: "law practice",
    value: "law practice"
  },
  {
    label: "leasing non-residential real estate",
    value: "leasing non-residential real estate"
  },
  {
    label: "leasing real estate",
    value: "leasing real estate"
  },
  {
    label: "leasing real estate agents and brokers",
    value: "leasing real estate agents and brokers"
  },
  {
    label: "leasing real estate and equipment rental services",
    value: "leasing real estate and equipment rental services"
  },
  {
    label: "leasing residential real estate",
    value: "leasing residential real estate"
  },
  {
    label: "leather product manufacturing",
    value: "leather product manufacturing"
  },
  {
    label: "legal services",
    value: "legal services"
  },
  {
    label: "legislative offices",
    value: "legislative offices"
  },
  {
    label: "leisure, travel & tourism",
    value: "leisure travel and tourism"
  },
  {
    label: "libraries",
    value: "libraries"
  },
  {
    label: "lime and gypsum products manufacturing",
    value: "lime and gypsum products manufacturing"
  },
  {
    label: "loan brokers",
    value: "loan brokers"
  },
  {
    label: "luxury goods & jewelry",
    value: "luxury goods and jewelry"
  },
  {
    label: "machinery manufacturing",
    value: "machinery manufacturing"
  },
  {
    label: "magnetic and optical media manufacturing",
    value: "magnetic and optical media manufacturing"
  },
  {
    label: "manufacturing",
    value: "manufacturing"
  },
  {
    label: "maritime",
    value: "maritime"
  },
  {
    label: "maritime transportation",
    value: "maritime transportation"
  },
  {
    label: "market research",
    value: "market research"
  },
  {
    label: "marketing services",
    value: "marketing services"
  },
  {
    label: "mattress and blinds manufacturing",
    value: "mattress and blinds manufacturing"
  },
  {
    label: "measuring and control instrument manufacturing",
    value: "measuring and control instrument manufacturing"
  },
  {
    label: "meat products manufacturing",
    value: "meat products manufacturing"
  },
  {
    label: "mechanical or industrial engineering",
    value: "mechanical or industrial engineering"
  },
  {
    label: "media and telecommunications",
    value: "media and telecommunications"
  },
  {
    label: "media production",
    value: "media production"
  },
  {
    label: "medical and diagnostic laboratories",
    value: "medical and diagnostic laboratories"
  },
  {
    label: "medical device",
    value: "medical device"
  },
  {
    label: "medical equipment manufacturing",
    value: "medical equipment manufacturing"
  },
  {
    label: "medical practices",
    value: "medical practices"
  },
  {
    label: "mental health care",
    value: "mental health care"
  },
  {
    label: "metal ore mining",
    value: "metal ore mining"
  },
  {
    label: "metal treatments",
    value: "metal treatments"
  },
  {
    label: "metal valve, ball, and roller manufacturing",
    value: "metal valve ball and roller manufacturing"
  },
  {
    label: "metalworking machinery manufacturing",
    value: "metalworking machinery manufacturing"
  },
  {
    label: "military and international affairs",
    value: "military and international affairs"
  },
  {
    label: "mining",
    value: "mining"
  },
  {
    label: "mobile computing software products",
    value: "mobile computing software products"
  },
  {
    label: "mobile food services",
    value: "mobile food services"
  },
  {
    label: "mobile gaming apps",
    value: "mobile gaming apps"
  },
  {
    label: "motor vehicle manufacturing",
    value: "motor vehicle manufacturing"
  },
  {
    label: "motor vehicle parts manufacturing",
    value: "motor vehicle parts manufacturing"
  },
  {
    label: "movies and sound recording",
    value: "movies and sound recording"
  },
  {
    label: "movies, videos and sound",
    value: "movies videos and sound"
  },
  {
    label: "movies, videos, and sound",
    value: "movies videos and sound"
  },
  {
    label: "museums",
    value: "museums"
  },
  {
    label: "museums, historical sites, and zoos",
    value: "museums historical sites and zoos"
  },
  {
    label: "music",
    value: "music"
  },
  {
    label: "musicians",
    value: "musicians"
  },
  {
    label: "nanotechnology research",
    value: "nanotechnology research"
  },
  {
    label: "natural gas distribution",
    value: "natural gas distribution"
  },
  {
    label: "natural gas extraction",
    value: "natural gas extraction"
  },
  {
    label: "newspaper publishing",
    value: "newspaper publishing"
  },
  {
    label: "non-profit organization management",
    value: "non-profit organization management"
  },
  {
    label: "non-profit organizations",
    value: "non-profit organizations"
  },
  {
    label: "nonmetallic mineral mining",
    value: "nonmetallic mineral mining"
  },
  {
    label: "nonresidential building construction",
    value: "nonresidential building construction"
  },
  {
    label: "nuclear electric power generation",
    value: "nuclear electric power generation"
  },
  {
    label: "nursing homes and residential care facilities",
    value: "nursing homes and residential care facilities"
  },
  {
    label: "office administration",
    value: "office administration"
  },
  {
    label: "office furniture and fixtures manufacturing",
    value: "office furniture and fixtures manufacturing"
  },
  {
    label: "oil and coal product manufacturing",
    value: "oil and coal product manufacturing"
  },
  {
    label: "oil and gas",
    value: "oil and gas"
  },
  {
    label: "oil extraction",
    value: "oil extraction"
  },
  {
    label: "oil, gas, and mining",
    value: "oil gas and mining"
  },
  {
    label: "online and mail order retail",
    value: "online and mail order retail"
  },
  {
    label: "online audio and video media",
    value: "online audio and video media"
  },
  {
    label: "online media",
    value: "online media"
  },
  {
    label: "operations consulting",
    value: "operations consulting"
  },
  {
    label: "optometrists",
    value: "optometrists"
  },
  {
    label: "outpatient care centers",
    value: "outpatient care centers"
  },
  {
    label: "outsourcing and offshoring consulting",
    value: "outsourcing and offshoring consulting"
  },
  {
    label: "outsourcing/offshoring",
    value: "outsourcing offshoring"
  },
  {
    label: "packaging & containers",
    value: "packaging and containers"
  },
  {
    label: "packaging and containers manufacturing",
    value: "packaging and containers manufacturing"
  },
  {
    label: "paint, coating, and adhesive manufacturing",
    value: "paint coating and adhesive manufacturing"
  },
  {
    label: "paper & forest products",
    value: "paper and forest products"
  },
  {
    label: "paper and forest product manufacturing",
    value: "paper and forest product manufacturing"
  },
  {
    label: "pension funds",
    value: "pension funds"
  },
  {
    label: "performing arts",
    value: "performing arts"
  },
  {
    label: "performing arts and spectator sports",
    value: "performing arts and spectator sports"
  },
  {
    label: "periodical publishing",
    value: "periodical publishing"
  },
  {
    label: "personal and laundry services",
    value: "personal and laundry services"
  },
  {
    label: "personal care product manufacturing",
    value: "personal care product manufacturing"
  },
  {
    label: "personal care services",
    value: "personal care services"
  },
  {
    label: "pet services",
    value: "pet services"
  },
  {
    label: "pharmaceutical manufacturing",
    value: "pharmaceutical manufacturing"
  },
  {
    label: "philanthropic fundraising services",
    value: "philanthropic fundraising services"
  },
  {
    label: "philanthropy",
    value: "philanthropy"
  },
  {
    label: "photography",
    value: "photography"
  },
  {
    label: "physical, occupational and speech therapists",
    value: "physical occupational and speech therapists"
  },
  {
    label: "physicians",
    value: "physicians"
  },
  {
    label: "pipeline transportation",
    value: "pipeline transportation"
  },
  {
    label: "plastics and rubber product manufacturing",
    value: "plastics and rubber product manufacturing"
  },
  {
    label: "plastics manufacturing",
    value: "plastics manufacturing"
  },
  {
    label: "political organizations",
    value: "political organizations"
  },
  {
    label: "postal services",
    value: "postal services"
  },
  {
    label: "primary and secondary education",
    value: "primary and secondary education"
  },
  {
    label: "primary metal manufacturing",
    value: "primary metal manufacturing"
  },
  {
    label: "printing services",
    value: "printing services"
  },
  {
    label: "professional organizations",
    value: "professional organizations"
  },
  {
    label: "professional services",
    value: "professional services"
  },
  {
    label: "professional training and coaching",
    value: "professional training and coaching"
  },
  {
    label: "program development",
    value: "program development"
  },
  {
    label: "public assistance programs",
    value: "public assistance programs"
  },
  {
    label: "public health",
    value: "public health"
  },
  {
    label: "public policy",
    value: "public policy"
  },
  {
    label: "public policy offices",
    value: "public policy offices"
  },
  {
    label: "public relations and communications services",
    value: "public relations and communications services"
  },
  {
    label: "public safety",
    value: "public safety"
  },
  {
    label: "publishing",
    value: "publishing"
  },
  {
    label: "racetracks",
    value: "racetracks"
  },
  {
    label: "radio and television broadcasting",
    value: "radio and television broadcasting"
  },
  {
    label: "rail transportation",
    value: "rail transportation"
  },
  {
    label: "railroad equipment manufacturing",
    value: "railroad equipment manufacturing"
  },
  {
    label: "ranching",
    value: "ranching"
  },
  {
    label: "ranching and fisheries",
    value: "ranching and fisheries"
  },
  {
    label: "real estate",
    value: "real estate"
  },
  {
    label: "real estate agents and brokers",
    value: "real estate agents and brokers"
  },
  {
    label: "real estate and equipment rental services",
    value: "real estate and equipment rental services"
  },
  {
    label: "recreational facilities",
    value: "recreational facilities"
  },
  {
    label: "regenerative design",
    value: "regenerative design"
  },
  {
    label: "religious institutions",
    value: "religious institutions"
  },
  {
    label: "renewable energy equipment manufacturing",
    value: "renewable energy equipment manufacturing"
  },
  {
    label: "renewable energy power generation",
    value: "renewable energy power generation"
  },
  {
    label: "renewable energy semiconductor manufacturing",
    value: "renewable energy semiconductor manufacturing"
  },
  {
    label: "renewables & environment",
    value: "renewables and environment"
  },
  {
    label: "repair and maintenance",
    value: "repair and maintenance"
  },
  {
    label: "research",
    value: "research"
  },
  {
    label: "research services",
    value: "research services"
  },
  {
    label: "residential building construction",
    value: "residential building construction"
  },
  {
    label: "restaurants",
    value: "restaurants"
  },
  {
    label: "retail",
    value: "retail"
  },
  {
    label: "retail apparel and fashion",
    value: "retail apparel and fashion"
  },
  {
    label: "retail appliances, electrical, and electronic equipment",
    value: "retail appliances electrical and electronic equipment"
  },
  {
    label: "retail art dealers",
    value: "retail art dealers"
  },
  {
    label: "retail art supplies",
    value: "retail art supplies"
  },
  {
    label: "retail bicycles",
    value: "retail bicycles"
  },
  {
    label: "retail books and printed news",
    value: "retail books and printed news"
  },
  {
    label: "retail building materials and garden equipment",
    value: "retail building materials and garden equipment"
  },
  {
    label: "retail firearms",
    value: "retail firearms"
  },
  {
    label: "retail fireworks",
    value: "retail fireworks"
  },
  {
    label: "retail florists",
    value: "retail florists"
  },
  {
    label: "retail footwear",
    value: "retail footwear"
  },
  {
    label: "retail furniture and home furnishings",
    value: "retail furniture and home furnishings"
  },
  {
    label: "retail gasoline",
    value: "retail gasoline"
  },
  {
    label: "retail groceries",
    value: "retail groceries"
  },
  {
    label: "retail health and personal care products",
    value: "retail health and personal care products"
  },
  {
    label: "retail luxury goods and jewelry",
    value: "retail luxury goods and jewelry"
  },
  {
    label: "retail motor vehicles",
    value: "retail motor vehicles"
  },
  {
    label: "retail music instruments",
    value: "retail music instruments"
  },
  {
    label: "retail musical instruments",
    value: "retail musical instruments"
  },
  {
    label: "retail office equipment",
    value: "retail office equipment"
  },
  {
    label: "retail office supplies and gifts",
    value: "retail office supplies and gifts"
  },
  {
    label: "retail pet products",
    value: "retail pet products"
  },
  {
    label: "retail pet supplies",
    value: "retail pet supplies"
  },
  {
    label: "retail pharmacies",
    value: "retail pharmacies"
  },
  {
    label: "retail recyclable materials & used merchandise",
    value: "retail recyclable materials and used merchandise"
  },
  {
    label: "retail sporting goods",
    value: "retail sporting goods"
  },
  {
    label: "retail sporting goods and bicycles",
    value: "retail sporting goods and bicycles"
  },
  {
    label: "retail telecommunications",
    value: "retail telecommunications"
  },
  {
    label: "retail tobacco",
    value: "retail tobacco"
  },
  {
    label: "retail toys and games",
    value: "retail toys and games"
  },
  {
    label: "reupholstery and furniture repair",
    value: "reupholstery and furniture repair"
  },
  {
    label: "robot manufacturing",
    value: "robot manufacturing"
  },
  {
    label: "robotics engineering",
    value: "robotics engineering"
  },
  {
    label: "rubber products manufacturing",
    value: "rubber products manufacturing"
  },
  {
    label: "satellite telecommunications",
    value: "satellite telecommunications"
  },
  {
    label: "savings institutions",
    value: "savings institutions"
  },
  {
    label: "school and employee bus services",
    value: "school and employee bus services"
  },
  {
    label: "seafood product manufacturing",
    value: "seafood product manufacturing"
  },
  {
    label: "secretarial schools",
    value: "secretarial schools"
  },
  {
    label: "securities and commodity exchanges",
    value: "securities and commodity exchanges"
  },
  {
    label: "security and investigations",
    value: "security and investigations"
  },
  {
    label: "security guards and patrol services",
    value: "security guards and patrol services"
  },
  {
    label: "security systems services",
    value: "security systems services"
  },
  {
    label: "semiconductor manufacturing",
    value: "semiconductor manufacturing"
  },
  {
    label: "semiconductors",
    value: "semiconductors"
  },
  {
    label: "services for renewable energy",
    value: "services for renewable energy"
  },
  {
    label: "services for the elderly and disabled",
    value: "services for the elderly and disabled"
  },
  {
    label: "sheet music publishing",
    value: "sheet music publishing"
  },
  {
    label: "shipbuilding",
    value: "shipbuilding"
  },
  {
    label: "shuttles and special needs transportation services",
    value: "shuttles and special needs transportation services"
  },
  {
    label: "sightseeing transportation",
    value: "sightseeing transportation"
  },
  {
    label: "skiing facilities",
    value: "skiing facilities"
  },
  {
    label: "smart meter manufacturing",
    value: "smart meter manufacturing"
  },
  {
    label: "soap and cleaning product manufacturing",
    value: "soap and cleaning product manufacturing"
  },
  {
    label: "social networking platforms",
    value: "social networking platforms"
  },
  {
    label: "software development",
    value: "software development"
  },
  {
    label: "solar electric power generation",
    value: "solar electric power generation"
  },
  {
    label: "sound recording",
    value: "sound recording"
  },
  {
    label: "space research and technology",
    value: "space research and technology"
  },
  {
    label: "specialty trade contractors",
    value: "specialty trade contractors"
  },
  {
    label: "spectator sports",
    value: "spectator sports"
  },
  {
    label: "sporting goods",
    value: "sporting goods"
  },
  {
    label: "sporting goods manufacturing",
    value: "sporting goods manufacturing"
  },
  {
    label: "sports and recreation instruction",
    value: "sports and recreation instruction"
  },
  {
    label: "sports teams and clubs",
    value: "sports teams and clubs"
  },
  {
    label: "spring and wire product manufacturing",
    value: "spring and wire product manufacturing"
  },
  {
    label: "staffing and recruiting",
    value: "staffing and recruiting"
  },
  {
    label: "steam and air-conditioning supply",
    value: "steam and air-conditioning supply"
  },
  {
    label: "strategic management services",
    value: "strategic management services"
  },
  {
    label: "subdivision of land",
    value: "subdivision of land"
  },
  {
    label: "sugar and confectionery product manufacturing",
    value: "sugar and confectionery product manufacturing"
  },
  {
    label: "surveying and mapping services",
    value: "surveying and mapping services"
  },
  {
    label: "tailor",
    value: "tailor"
  },
  {
    label: "taxi and limousine services",
    value: "taxi and limousine services"
  },
  {
    label: "technical and vocational training",
    value: "technical and vocational training"
  },
  {
    label: "technology, information and internet",
    value: "technology information and internet"
  },
  {
    label: "technology, information and media",
    value: "technology information and media"
  },
  {
    label: "telecommunications",
    value: "telecommunications"
  },
  {
    label: "telecommunications carriers",
    value: "telecommunications carriers"
  },
  {
    label: "telephone call centers",
    value: "telephone call centers"
  },
  {
    label: "temporary help services",
    value: "temporary help services"
  },
  {
    label: "textile manufacturing",
    value: "textile manufacturing"
  },
  {
    label: "theater companies",
    value: "theater companies"
  },
  {
    label: "think tanks",
    value: "think tanks"
  },
  {
    label: "tobacco",
    value: "tobacco"
  },
  {
    label: "tobacco manufacturing",
    value: "tobacco manufacturing"
  },
  {
    label: "toys",
    value: "toys"
  },
  {
    label: "translation and localization",
    value: "translation and localization"
  },
  {
    label: "transportation equipment manufacturing",
    value: "transportation equipment manufacturing"
  },
  {
    label: "transportation programs",
    value: "transportation programs"
  },
  {
    label: "transportation, logistics, supply chain and storage",
    value: "transportation logistics supply chain and storage"
  },
  {
    label: "transportation/trucking/railroad",
    value: "transportation trucking railroad"
  },
  {
    label: "travel arrangements",
    value: "travel arrangements"
  },
  {
    label: "truck transportation",
    value: "truck transportation"
  },
  {
    label: "trusts and estates",
    value: "trusts and estates"
  },
  {
    label: "turned products and fastener manufacturing",
    value: "turned products and fastener manufacturing"
  },
  {
    label: "urban transit services",
    value: "urban transit services"
  },
  {
    label: "utilities",
    value: "utilities"
  },
  {
    label: "utilities administration",
    value: "utilities administration"
  },
  {
    label: "utility system construction",
    value: "utility system construction"
  },
  {
    label: "vehicle hire",
    value: "vehicle hire"
  },
  {
    label: "vehicle repair and maintenance",
    value: "vehicle repair and maintenance"
  },
  {
    label: "venture capital and private equity principals",
    value: "venture capital and private equity principals"
  },
  {
    label: "veterinary",
    value: "veterinary"
  },
  {
    label: "veterinary services",
    value: "veterinary services"
  },
  {
    label: "vocational rehabilitation services",
    value: "vocational rehabilitation services"
  },
  {
    label: "warehousing",
    value: "warehousing"
  },
  {
    label: "warehousing and storage",
    value: "warehousing and storage"
  },
  {
    label: "waste collection",
    value: "waste collection"
  },
  {
    label: "waste treatment and disposal",
    value: "waste treatment and disposal"
  },
  {
    label: "water supply and irrigation systems",
    value: "water supply and irrigation systems"
  },
  {
    label: "water, waste, steam, and air conditioning services",
    value: "water waste steam and air conditioning services"
  },
  {
    label: "wellness and fitness services",
    value: "wellness and fitness services"
  },
  {
    label: "wholesale",
    value: "wholesale"
  },
  {
    label: "wholesale alcoholic beverages",
    value: "wholesale alcoholic beverages"
  },
  {
    label: "wholesale apparel and sewing supplies",
    value: "wholesale apparel and sewing supplies"
  },
  {
    label: "wholesale appliances, electrical, and electronics",
    value: "wholesale appliances electrical and electronics"
  },
  {
    label: "wholesale building materials",
    value: "wholesale building materials"
  },
  {
    label: "wholesale chemical and allied products",
    value: "wholesale chemical and allied products"
  },
  {
    label: "wholesale computer equipment",
    value: "wholesale computer equipment"
  },
  {
    label: "wholesale drugs and sundries",
    value: "wholesale drugs and sundries"
  },
  {
    label: "wholesale food and beverage",
    value: "wholesale food and beverage"
  },
  {
    label: "wholesale footwear",
    value: "wholesale footwear"
  },
  {
    label: "wholesale furniture and home furnishings",
    value: "wholesale furniture and home furnishings"
  },
  {
    label: "wholesale hardware, plumbing, heating equipment",
    value: "wholesale hardware plumbing heating equipment"
  },
  {
    label: "wholesale import and export",
    value: "wholesale import and export"
  },
  {
    label: "wholesale leather goods and jewelry",
    value: "wholesale leather goods and jewelry"
  },
  {
    label: "wholesale luxury goods and jewelry",
    value: "wholesale luxury goods and jewelry"
  },
  {
    label: "wholesale machinery",
    value: "wholesale machinery"
  },
  {
    label: "wholesale medical supplies",
    value: "wholesale medical supplies"
  },
  {
    label: "wholesale metals and minerals",
    value: "wholesale metals and minerals"
  },
  {
    label: "wholesale motor vehicles and parts",
    value: "wholesale motor vehicles and parts"
  },
  {
    label: "wholesale paper products",
    value: "wholesale paper products"
  },
  {
    label: "wholesale petroleum and petroleum products",
    value: "wholesale petroleum and petroleum products"
  },
  {
    label: "wholesale photography equipment and supplies",
    value: "wholesale photography equipment and supplies"
  },
  {
    label: "wholesale raw farm products",
    value: "wholesale raw farm products"
  },
  {
    label: "wholesale recyclable materials",
    value: "wholesale recyclable materials"
  },
  {
    label: "wind electric power generation",
    value: "wind electric power generation"
  },
  {
    label: "wine & spirits",
    value: "wine and spirits"
  },
  {
    label: "wineries",
    value: "wineries"
  },
  {
    label: "wireless services",
    value: "wireless services"
  },
  {
    label: "women's handbag manufacturing",
    value: "women's handbag manufacturing"
  },
  {
    label: "wood product manufacturing",
    value: "wood product manufacturing"
  },
  {
    label: "writing and editing",
    value: "writing and editing"
  },
  {
    label: "zoos and botanical gardens",
    value: "zoos and botanical gardens"
  }
]

export const LOCATION_OPTIONS = [
  // United States
  { label: "New York, NY, US", value: "New York, NY, US" },
  { label: "Los Angeles, CA, US", value: "Los Angeles, CA, US" },
  { label: "Chicago, IL, US", value: "Chicago, IL, US" },
  { label: "Houston, TX, US", value: "Houston, TX, US" },
  { label: "Phoenix, AZ, US", value: "Phoenix, AZ, US" },
  { label: "Philadelphia, PA, US", value: "Philadelphia, PA, US" },
  { label: "San Antonio, TX, US", value: "San Antonio, TX, US" },
  { label: "San Diego, CA, US", value: "San Diego, CA, US" },
  { label: "Dallas, TX, US", value: "Dallas, TX, US" },
  { label: "San Francisco, CA, US", value: "San Francisco, CA, US" },

  // India
  { label: "Mumbai, MH, IN", value: "Mumbai, MH, IN" },
  { label: "Delhi, DL, IN", value: "Delhi, DL, IN" },
  { label: "Bengaluru, KA, IN", value: "Bengaluru, KA, IN" },
  { label: "Hyderabad, TG, IN", value: "Hyderabad, TG, IN" },
  { label: "Chennai, TN, IN", value: "Chennai, TN, IN" },
  { label: "Kolkata, WB, IN", value: "Kolkata, WB, IN" },
  { label: "Pune, MH, IN", value: "Pune, MH, IN" },
  { label: "Ahmedabad, GJ, IN", value: "Ahmedabad, GJ, IN" },
  { label: "Jaipur, RJ, IN", value: "Jaipur, RJ, IN" },
  { label: "Lucknow, UP, IN", value: "Lucknow, UP, IN" },

  // Canada
  { label: "Toronto, ON, CA", value: "Toronto, ON, CA" },
  { label: "Vancouver, BC, CA", value: "Vancouver, BC, CA" },
  { label: "Montreal, QC, CA", value: "Montreal, QC, CA" },
  { label: "Calgary, AB, CA", value: "Calgary, AB, CA" },
  { label: "Ottawa, ON, CA", value: "Ottawa, ON, CA" },
  { label: "Edmonton, AB, CA", value: "Edmonton, AB, CA" },
  { label: "Winnipeg, MB, CA", value: "Winnipeg, MB, CA" },
  { label: "Quebec City, QC, CA", value: "Quebec City, QC, CA" },
  { label: "Hamilton, ON, CA", value: "Hamilton, ON, CA" },
  { label: "Halifax, NS, CA", value: "Halifax, NS, CA" },

  // United Kingdom
  { label: "London, ENG, GB", value: "London, ENG, GB" },
  { label: "Manchester, ENG, GB", value: "Manchester, ENG, GB" },
  { label: "Birmingham, ENG, GB", value: "Birmingham, ENG, GB" },
  { label: "Liverpool, ENG, GB", value: "Liverpool, ENG, GB" },
  { label: "Leeds, ENG, GB", value: "Leeds, ENG, GB" },
  { label: "Bristol, ENG, GB", value: "Bristol, ENG, GB" },
  { label: "Glasgow, SCT, GB", value: "Glasgow, SCT, GB" },
  { label: "Edinburgh, SCT, GB", value: "Edinburgh, SCT, GB" },
  { label: "Sheffield, ENG, GB", value: "Sheffield, ENG, GB" },
  { label: "Leicester, ENG, GB", value: "Leicester, ENG, GB" },

  // Australia
  { label: "Sydney, NSW, AU", value: "Sydney, NSW, AU" },
  { label: "Melbourne, VIC, AU", value: "Melbourne, VIC, AU" },
  { label: "Brisbane, QLD, AU", value: "Brisbane, QLD, AU" },
  { label: "Perth, WA, AU", value: "Perth, WA, AU" },
  { label: "Adelaide, SA, AU", value: "Adelaide, SA, AU" },
  { label: "Canberra, ACT, AU", value: "Canberra, ACT, AU" },
  { label: "Gold Coast, QLD, AU", value: "Gold Coast, QLD, AU" },
  { label: "Newcastle, NSW, AU", value: "Newcastle, NSW, AU" },
  { label: "Hobart, TAS, AU", value: "Hobart, TAS, AU" },
  { label: "Darwin, NT, AU", value: "Darwin, NT, AU" },

  // Germany
  { label: "Berlin, BE, DE", value: "Berlin, BE, DE" },
  { label: "Munich, BY, DE", value: "Munich, BY, DE" },
  { label: "Hamburg, HH, DE", value: "Hamburg, HH, DE" },
  { label: "Frankfurt, HE, DE", value: "Frankfurt, HE, DE" },
  { label: "Stuttgart, BW, DE", value: "Stuttgart, BW, DE" },
  { label: "Cologne, NW, DE", value: "Cologne, NW, DE" },
  { label: "Düsseldorf, NW, DE", value: "Düsseldorf, NW, DE" },
  { label: "Leipzig, SN, DE", value: "Leipzig, SN, DE" },
  { label: "Dresden, SN, DE", value: "Dresden, SN, DE" },
  { label: "Bremen, HB, DE", value: "Bremen, HB, DE" },

  // France
  { label: "Paris, IDF, FR", value: "Paris, IDF, FR" },
  { label: "Lyon, ARA, FR", value: "Lyon, ARA, FR" },
  { label: "Marseille, PACA, FR", value: "Marseille, PACA, FR" },
  { label: "Toulouse, OCC, FR", value: "Toulouse, OCC, FR" },
  { label: "Nice, PACA, FR", value: "Nice, PACA, FR" },
  { label: "Nantes, PDL, FR", value: "Nantes, PDL, FR" },
  { label: "Strasbourg, GE, FR", value: "Strasbourg, GE, FR" },
  { label: "Montpellier, OCC, FR", value: "Montpellier, OCC, FR" },
  { label: "Bordeaux, NAQ, FR", value: "Bordeaux, NAQ, FR" },
  { label: "Lille, HDF, FR", value: "Lille, HDF, FR" },

  // Japan
  { label: "Tokyo, TK, JP", value: "Tokyo, TK, JP" },
  { label: "Osaka, OS, JP", value: "Osaka, OS, JP" },
  { label: "Kyoto, KY, JP", value: "Kyoto, KY, JP" },
  { label: "Yokohama, KN, JP", value: "Yokohama, KN, JP" },
  { label: "Nagoya, AI, JP", value: "Nagoya, AI, JP" },
  { label: "Sapporo, HK, JP", value: "Sapporo, HK, JP" },
  { label: "Fukuoka, FK, JP", value: "Fukuoka, FK, JP" },
  { label: "Kobe, HG, JP", value: "Kobe, HG, JP" },
  { label: "Hiroshima, HS, JP", value: "Hiroshima, HS, JP" },
  { label: "Sendai, MG, JP", value: "Sendai, MG, JP" },
];


// company size options
export const COMPANY_SIZE_OPTIONS = [
  { label: "Small Business(1-10)", value: "1-10" },
  { label: "Medium Business(11-50)", value: "11-50" },
  { label: "Large Business(51-200)", value: "51-200" },
  { label: "Enterprise(201-500)", value: "201-500" },
  { label: "Enterprise(500+)", value: "500+" },
];

/** Explorium /prospect-search shaped filters (chip lists + toggles). NL query is built from these when applying. */
export const ProspectSearchFilterFields = [
  // {
  //   id: 100,
  //   key: "business_id",
  //   title: "Business ID",
  //   icon: <Building2 size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "Exact Explorium business IDs",
  // },
  {
    id: 100,
    key: "has_phone_number",
    title: "Prefer prospects with phone",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "toggle",
  },
  {
    id: 101,
    key: "has_email",
    title: "Prefer prospects with email",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "toggle",
  },
  {
    id: 102,
    key: "job_title",
    title: "Job Title",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "e.g. Vice President of Sales",
  },
  {
    id: 112,
    key: "country_code",
    title: "Country code",
    icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Only if company country codes empty",
  },
  {
    id: 104,
    key: "job_level",
    title: "Job level",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "e.g. vice president, director",
  },
  {
    id: 105,
    key: "job_department",
    title: "Department",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "e.g. C-suite, Engineering",
  },
  {
    id: 114,
    key: "linkedin_category",
    title: "Industry",
    icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Only one industry per search",
    options: linkedinCategories,
  },
  {
    id: 113,
    key: "city_region_country",
    title: "City / region / country text",
    icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Free-form location phrases",
    options: LOCATION_OPTIONS,
  },


  {
    id: 106,
    key: "company_name",
    title: "Company name",
    icon: <Building2 size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "e.g. Snowflake",
  },
  {
    id: 107,
    key: "company_size",
    title: "Employee Size",
    icon: <Users size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "e.g. 1-50, 51-200, 201-1000, 1001-5000, 5001-10000, 10001+",
    options: COMPANY_SIZE_OPTIONS,
  },

  {
    id: 108,
    key: "company_revenue",
    title: "Company revenue",
    icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "dropDown",
    placeholder: "Explorium revenue buckets as text",
  },
  // {
  //   id: 109,
  //   key: "country_code",
  //   title: "Country codes",
  //   icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "e.g. US (prefer this OR region, not both)",
  // },
  // {
  //   id: 110,
  //   key: "region_country_code",
  //   title: "Region / country codes",
  //   icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "Only if country codes empty",
  // },
  // {
  //   id: 111,
  //   key: "company_country_code",
  //   title: "Company country codes",
  //   icon: <MapPin size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "HQ/company country codes",
  // },
  {
    id: 103,
    key: "job_title_include_related",
    title: "Include related job titles",
    icon: <Briefcase size={16} className="text-gray-700 dark:text-gray-300" />,
    type: "toggle",
  },


  // {
  //   id: 114,
  //   key: "google_category",
  //   title: "Google category",
  //   icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "Leave LinkedIn category empty",
  // },
  // {
  //   id: 115,
  //   key: "naics_category",
  //   title: "NAICS codes",
  //   icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "Numeric NAICS codes",
  // },
  // {
  //   id: 115,
  //   key: "industry",
  //   title: "Industry",
  //   icon: <Factory size={16} className="text-gray-700 dark:text-gray-300" />,
  //   type: "dropDown",
  //   placeholder: "Industry labels (e.g. from ICP)",
  // },


];

export const PROSPECT_SEARCH_DEFAULT_FILTERS = {
  business_id: [],
  job_title: [],
  job_title_include_related: false,
  job_level: [],
  job_department: [],
  company_name: [],
  company_size: [],
  company_revenue: [],
  country_code: [],
  region_country_code: [],
  company_country_code: [],
  company_region_country_code: [],
  city_region_country: [],
  linkedin_category: [],
  google_category: [],
  // naics_category: [],
  industry: [],
  has_email: true,
  has_phone_number: false,
};




export const renderContactCell = (
  item,
  columnKey,
  handleDelete,
  handleEdit,
  onUpdateConsent,
) => {
  switch (columnKey) {
    case "name":
      return (
        <Link
          to={`/contacts/${item.id}`}
          className="block w-full text-wrap break-words hover:opacity-80"
          aria-label={`View ${item?.name || "contact"}`}
        >
          <ProspectProfileRow
            key={item.id}
            id={item.id}
            avatarUrl={item?.avatarUrl}
            name={item?.name}
            email={item?.email}
            number={90}
          />
        </Link>
      );
    case "company":
      return (
        <p className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words">
          {item.company !== "" ? item.company : "N/A"}
        </p>
      );
    case "emailConsentStatus": {
      const status = String(item?.emailConsentStatus || "unknown").toLowerCase();
      const labels = { consented: "Consented", unsubscribed: "Unsubscribed", unknown: "Unknown" };
      const variants = { consented: "success", unsubscribed: "warning", unknown: "neutral" };
      return (
        <div className="flex items-center gap-1.5" onClick={(event) => event.stopPropagation()}>
          <StatusChip label={labels[status] || "Unknown"} variant={variants[status] || "neutral"} />
          {onUpdateConsent && (
            <button
              type="button"
              aria-label={`Update email consent for ${item?.name || "contact"}`}
              title="Update email consent"
              onClick={() => onUpdateConsent(item)}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-secondary dark:hover:bg-gray-800"
            >
              <Pencil size={13} />
            </button>
          )}
        </div>
      );
    }
    case "matchScore": {
      const hasBreakdown = item?.leadScoreBreakdown?.length > 0;
      const score = item?.matchScore;

      const isEmpty =
        score === "" ||
        score === null ||
        score === undefined ||
        (typeof score === "number" && (Number.isNaN(score) || score === 0));

      if (isEmpty && !hasBreakdown) {
        return (
          <div className="flex justify-center items-center">
            <GenerateMatchScore item={item} />
          </div>
        );
      }

      const aggregateMeta = !isEmpty ? getAggregateMatchFitMeta(score) : null;
      const displayNum =
        typeof score === "number" && !Number.isNaN(score)
          ? Math.round(score)
          : score;

      const tooltipContent = hasBreakdown ? (
        <div className="flex max-h-[min(280px,50vh)] flex-col gap-2.5 overflow-y-auto overscroll-contain pr-1 touch-pan-y [-webkit-overflow-scrolling:touch]">
          {item.leadScoreBreakdown.map((row, idx) => {
            const rowMeta = getMatchScoreFitMeta(row.matchScore);
            return (
              <section
                key={`${row.campaignId || "c"}-${idx}`}
                className="rounded-xl border border-gray-100 bg-gradient-to-b from-gray-50/80 to-white p-3 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 gap-2">
                    <Megaphone
                      size={15}
                      strokeWidth={2}
                      className="mt-0.5 shrink-0 text-secondary"
                      aria-hidden
                    />
                    <p className="break-words text-[13px] font-semibold leading-snug text-gray-900">
                      {row.campaignName || "Campaign"}
                    </p>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums ring-1 ${rowMeta
                      ? getMatchScoreFitKeyPillClasses(rowMeta.key)
                      : getLeadScaleScorePillClasses(row.matchScore)
                      }`}
                  >
                    {rowMeta
                      ? `${rowMeta.label} · ${row.matchScore}`
                      : String(row.matchScore)}
                  </span>
                </div>
                {row.reasoning ? (
                  <div className="mt-2.5 border-l-[3px] border-secondary pl-3 text-[11px] break-words">
                    {renderMatchScoreReasoning(
                      row.reasoning,
                      `${row.campaignId || "c"}-${idx}`,
                    )}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : (
        item?.matchScoreReason || "N/A"
      );

      const chipLabel =
        isEmpty || !aggregateMeta
          ? "N/A"
          : `${aggregateMeta.label} · ${displayNum}`;
      const chipVariant =
        isEmpty || !aggregateMeta
          ? "neutral"
          : aggregateMeta.key === "strong"
            ? "success"
            : aggregateMeta.key === "moderate"
              ? "warning"
              : aggregateMeta.key === "not_fit"
                ? "danger"
                : "neutral";

      return (
        <StatusChip
          label={chipLabel}
          variant={chipVariant}
          tooltipTitle={hasBreakdown ? "Match score by campaign" : undefined}
          tooltipPanelClassName={
            hasBreakdown
              ? "max-w-[min(384px,calc(100vw-32px))] border-gray-100/90 px-3 py-3 shadow-xl shadow-gray-900/[0.07]"
              : undefined
          }
          tooltipContent={tooltipContent}
          tooltipCloseDelay={hasBreakdown ? 500 : undefined}
        />
      );
    }
    case "industry": {
      const val = item.industry ?? "";
      const firstIndustry = val.includes(",") ? val.split(",")[0].trim() : val;
      return (
        <p className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words">
          {firstIndustry || "N/A"}
        </p>
      );
    }
    case "engagementScore": {
      const temperature = item?.engagementScoreReason?.temperature || "cold";
      return (
        <StatusChip
          label={
            item?.engagementScore === "N/A"
              ? "N/A"
              : `${getTextInCapitalize(temperature)} · ${item?.engagementScore}`
          }
          variant={
            item?.engagementScore === "N/A"
              ? "neutral"
              : getEngagementStatusVariant(temperature)
          }
          tooltipTitle="Engagement breakdown"
          tooltipPanelClassName="max-w-[min(352px,calc(100vw-32px))] px-3 py-3"
          tooltipContent={
            <EngagementScoreBreakdown
              score={item?.engagementScore === "N/A" ? 0 : item?.engagementScore}
              breakdown={item?.engagementScoreReason}
              campaignName={item?.engagementScoreCampaignName}
            />
          }
          tooltipCloseDelay={500}
        />
      );
    }
    case "linkedinUrl": {
      const url = item.linkedinUrl;
      if (!url || String(url).trim() === "") {
        return <p className="text-xs text-gray-500 dark:text-gray-400">N/A</p>;
      }
      return <LinkedinButton linkedinUrl={url} />;
    }
    case "actions": {
      const dropdownActions = [
        {
          id: 1,
          name: "Edit",
          onClick: () => handleEdit?.(item),
          icon: <Pencil size={14} className="text-gray-500" />,
        },
        {
          id: 2,
          name: "Delete",
          onClick: () => handleDelete?.(item.id),
          icon: <Trash2 size={14} className="text-red-500" />,
        },
      ];
      return (
        <div className="flex items-center justify-center">
          <CommonDropdownMenu
            actions={dropdownActions}
            itemId={item.id}
            iconSize={16}
            className="min-w-[95px]"
          />
        </div>
      );
    }
    case "whatsappNumber":
    case "whatsapp_number": {
      const rawVal = item.whatsappNumber ?? item.whatsapp_number;
      const cleanVal =
        rawVal !== null && rawVal !== undefined
          ? String(rawVal).replace(/^['"]+/g, "").trim()
          : "";
      const isValid = cleanVal !== "" && cleanVal !== "N/A";
      return (
        <p className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words">
          {isValid ? cleanVal : "N/A"}
        </p>
      );
    }
    default: {
      const rawVal = item[columnKey];
      const cleanVal =
        rawVal !== null && rawVal !== undefined
          ? String(rawVal).replace(/^['"]+/g, "").trim()
          : "";
      const isValidValue = cleanVal !== "" && cleanVal !== "N/A";
      return (
        <p className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words">
          {isValidValue ? cleanVal : "N/A"}
        </p>
      );
    }
  }
};

/** `value` matches `filterUrl` / contacts `groupBy` mode. `Icon` is a Lucide icon component. */
export const CONTACTS_GROUP_BY_OPTIONS = [
  { value: "accounts", label: "Accounts", Icon: Building2 },
  { value: "industry", label: "Industry", Icon: Factory },
  { value: "campaigns", label: "Campaigns", Icon: Megaphone },
  { value: "match_fit", label: "Match score", Icon: Target },
  { value: "created_date", label: "Created date", Icon: Calendar },
];

const CONTACT_MATCH_FIT_GROUP_ORDER = [
  "Strong Fit",
  "Medium Fit",
  "No Fit",
  "Unavailable",
];

export function compareContactMatchFitGroupLabels(a, b) {
  const ia = CONTACT_MATCH_FIT_GROUP_ORDER.indexOf(a);
  const ib = CONTACT_MATCH_FIT_GROUP_ORDER.indexOf(b);
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
}

export function compareContactCreatedDateGroupLabels(a, b) {
  const parseGroupDate = (label) => {
    const timestamp = Date.parse(label);
    return Number.isNaN(timestamp) ? -Infinity : timestamp;
  };

  return parseGroupDate(b) - parseGroupDate(a);
}

/** Shown on the group-by control when no mode is selected. */
export const CONTACTS_GROUP_BY_PLACEHOLDER_ICON = LayoutGrid;

// move to options
export const MOVE_TO_OPTIONS = [
  { label: "Move to Campaigns", value: "move_to_campaigns" },
  { label: "Move to Accounts", value: "move_to_accounts" },
  { label: "Move to Both", value: "move_to_both" },
];
